/**
 * Triage page — the vendor-side CRA workflow.
 *
 * A VulnerabilityEvent is created by the KEV scan whenever an actively exploited
 * CVE appears in a KomponenteVersion that ships inside one of our products. It is
 * assessed bottom-up (DEV-100): the component verdict proposes the verdict of
 * every product version containing it, a confirmed product verdict AFFECTED
 * fans out Benachrichtigung cards for the instances running that version, and
 * each instance can be refined with environment factors and a priority. Only
 * those cards can be stamped as sent — that chain is the evidence trail for the
 * market surveillance authority.
 *
 * Contract details that shape the code below:
 *   - Every timestamp in this payload is a RAW `CmdbDateTime` (ISO,
 *     'dd/MM/yyyy HH:mm:ss' or epoch millis). All of them go through
 *     `fmtDate`/`fmtDay`; `new Date(x)` alone would render 'Invalid Date'.
 *   - `cve.base_score` may be a string — compared only via `parseScore`.
 *   - One row per component hit; `assessment` carries the progress of the
 *     product and instance levels. The dialog loads all three levels itself.
 *   - After a verdict or notification mutation the affected row is REFETCHED
 *     from the queue and patched into state. Advisory drafts and evidence belong
 *     to the product assessment and use its endpoints in the dialog.
 *   - Mutating controls are gated on `triage.write` / `notification.send`. An
 *     auditor sees every row, the full audit trail and every draft, and no
 *     enabled action.
 */

import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router-dom';

import CveLink from '../components/CveLink';
import Banner from '../components/Banner';
import Chip, { ChipList } from '../components/Chip';
import CvssScore from '../components/CvssScore';
import DataTable, { type DataTableColumn } from '../components/DataTable';
import EmptyState from '../components/EmptyState';
import { KpiRow, type KpiItem } from '../components/KpiTile';
import PageHeader from '../components/PageHeader';
import { PageSpinner } from '../components/Spinner';
import { useToast } from '../components/Toast';
import { errorMessage, fetchVulnerabilityEvents } from '../lib/api';
import { useAuth } from '../lib/auth';
import { DASH, countLabel, fmtDate, fmtDay, fmtValue, truncate } from '../lib/format';
import { missingPermissionTitle, readOnlyNotice } from '../lib/permissions';
import type {
  Bewertung,
  KpiCounter,
  VulnerabilityEventRow,
  VulnerabilityEventsResponse,
} from '../lib/types';

import AssessmentDialog, { type AssessmentTab } from './triage/AssessmentDialog';
import {
  EventAuditTrail,
  EventCveFacts,
  EventInstanzen,
  EventScopeFacts,
} from './triage/EventFacts';
import {
  BEWERTUNG_LABELS,
  BEWERTUNG_TONE,
  kundenOf,
  matchesSearch,
  parseScore,
  produktVersionLabel,
} from './triage/eventHelpers';

import './triage/triage.css';

/** How many customer names fit into a table cell before the '+N' overflow. */
const KUNDEN_VISIBLE = 3;

const SCORE_OPTIONS = [
  { value: '', label: 'Min. CVSS: alle' },
  { value: '4', label: '≥ 4,0 (mittel)' },
  { value: '7', label: '≥ 7,0 (hoch)' },
  { value: '9', label: '≥ 9,0 (kritisch)' },
] as const;

/**
 * Which assessments the queue shows: a component verdict, MAIL = notification
 * still to send, REVIEW = a level is marked „Erneut prüfen“ or contradicts the
 * sent notifications, PRODUCTS = a product version still lacks its verdict.
 */
type BewertungFilter = Bewertung | 'MAIL' | 'REVIEW' | 'PRODUCTS' | 'ALLE';

interface Filters {
  bewertung: BewertungFilter;
  search: string;
  /** Empty string = no CVSS floor. Kept as a string because it comes from a <select>. */
  minScore: string;
  /** 'YYYY-MM-DD' — events whose KEV date is older are hidden. */
  kevFrom: string;
  onlyKevListed: boolean;
  /** Minimum number of distinct affected customers. */
  minKunden: string;
}

const INITIAL_FILTERS: Filters = {
  bewertung: 'OFFEN',
  search: '',
  minScore: '',
  kevFrom: '',
  onlyKevListed: false,
  minKunden: '',
};

/**
 * One select for assessment and the KEV-list flag. Every assessment is a
 * choice of its own; „KEV-gelistet“ combines with „Offen“ and „Alle“ as before.
 */
type StatusFilter =
  | 'open' | 'open-kev' | 'pruefung' | 'products' | 'review' | 'betroffen' | 'mail' | 'nicht'
  | 'behoben' | 'all' | 'kev';

const STATUS_FILTERS: Readonly<Record<StatusFilter, { label: string; flags: Pick<Filters, 'bewertung' | 'onlyKevListed'> }>> = {
  open: { label: 'Nur offene', flags: { bewertung: 'OFFEN', onlyKevListed: false } },
  'open-kev': { label: 'Offene, KEV-gelistet', flags: { bewertung: 'OFFEN', onlyKevListed: true } },
  pruefung: { label: 'In Prüfung', flags: { bewertung: 'UNDER_INVESTIGATION', onlyKevListed: false } },
  products: { label: 'Produktbewertung offen', flags: { bewertung: 'PRODUCTS', onlyKevListed: false } },
  review: { label: 'Erneut prüfen', flags: { bewertung: 'REVIEW', onlyKevListed: false } },
  betroffen: { label: 'Betroffen', flags: { bewertung: 'BETROFFEN', onlyKevListed: false } },
  mail: { label: 'Benachrichtigung offen', flags: { bewertung: 'MAIL', onlyKevListed: false } },
  nicht: { label: 'Nicht betroffen', flags: { bewertung: 'NICHT_BETROFFEN', onlyKevListed: false } },
  behoben: { label: 'Vom Scan geschlossen', flags: { bewertung: 'BEHOBEN', onlyKevListed: false } },
  all: { label: 'Alle Bewertungen', flags: { bewertung: 'ALLE', onlyKevListed: false } },
  kev: { label: 'Alle, KEV-gelistet', flags: { bewertung: 'ALLE', onlyKevListed: true } },
};

function sameFilters(a: Filters, b: Filters): boolean {
  return (Object.keys(a) as (keyof Filters)[]).every((key) => a[key] === b[key]);
}

function statusFilterValue(filters: Filters): StatusFilter {
  const keys = Object.keys(STATUS_FILTERS) as StatusFilter[];
  return (
    keys.find((key) => {
      const flags = STATUS_FILTERS[key].flags;
      return flags.bewertung === filters.bewertung && flags.onlyKevListed === filters.onlyKevListed;
    }) ??
    keys.find((key) => STATUS_FILTERS[key].flags.bewertung === filters.bewertung) ??
    'all'
  );
}

/** A level above the component is marked for review or contradicts sent notifications. */
function needsReview(event: VulnerabilityEventRow): boolean {
  const a = event.assessment;
  return a != null && (a.products.needs_review + a.instances.needs_review + a.products.contradictions) > 0;
}

/** A product version containing the component still lacks a confirmed verdict. */
function productsOpen(event: VulnerabilityEventRow): boolean {
  const a = event.assessment;
  return a != null && a.products.confirmed < a.products.total;
}

function matchesBewertung(event: VulnerabilityEventRow, bewertung: BewertungFilter): boolean {
  if (bewertung === 'ALLE') return true;
  if (bewertung === 'MAIL') return event.benachrichtigungen_offen > 0;
  if (bewertung === 'REVIEW') return needsReview(event);
  if (bewertung === 'PRODUCTS') return productsOpen(event);
  return event.bewertung === bewertung;
}

function sumBy(
  events: readonly VulnerabilityEventRow[],
  pick: (event: VulnerabilityEventRow) => number,
): number {
  return events.reduce((total, event) => total + pick(event), 0);
}

/** Parses a filter input that may be empty or junk into a numeric floor. */
function numericFloor(raw: string): number | null {
  if (raw.trim() === '') return null;
  const parsed = Number.parseFloat(raw.replace(',', '.'));
  return Number.isFinite(parsed) ? parsed : null;
}

/** Current native KEV state; unlike the CVE card flag this is not persisted. */
function hasNativeKevAssertion(event: VulnerabilityEventRow): boolean {
  return event.kev.sources.some((source) => Boolean(source?.trim()));
}

export function TriagePage(): ReactNode {
  const { user, has } = useAuth();
  const toast = useToast();

  const [data, setData] = useState<VulnerabilityEventsResponse | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadedAt, setLoadedAt] = useState<Date | null>(null);
  const [searchParams] = useSearchParams();
  // `?q=` deep links (from the Audit-Log) open the queue on one case, decided
  // or not — so the 'Nur offene' default must not hide it.
  const [filters, setFilters] = useState<Filters>(() => {
    const q = searchParams.get('q')?.trim() ?? '';
    return q === '' ? INITIAL_FILTERS : { ...INITIAL_FILTERS, bewertung: 'ALLE', search: q };
  });
  const [bewertungEventId, setBewertungEventId] = useState<number | null>(null);
  const [assessmentTab, setAssessmentTab] = useState<AssessmentTab>('component');

  // Guards a late response from overwriting a newer one.
  const requestSeq = useRef(0);

  const canTriage = has('triage.write');
  const canSend = has('notification.send');
  const readOnly = !canTriage && !canSend;
  const bewerter = user?.name?.trim() || user?.email || 'unbekannt';

  const load = useCallback(async (): Promise<void> => {
    const seq = ++requestSeq.current;
    setLoading(true);
    try {
      const response = await fetchVulnerabilityEvents();
      if (seq !== requestSeq.current) return;
      setData(response);
      setLoadError(null);
      setLoadedAt(new Date());
    } catch (err) {
      if (seq !== requestSeq.current) return;
      setLoadError(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  /**
   * Refetch after a mutation and patch exactly the affected row back into state.
   * There is no per-event GET, so the list is the source; patching one row keeps
   * the other rows, the sort order and the expanded details stable while still
   * showing only server-confirmed values.
   */
  const refreshEvent = useCallback(async (eventId: number): Promise<void> => {
    const seq = ++requestSeq.current;
    const fresh = await fetchVulnerabilityEvents();
    // A full reload started meanwhile owns the state — drop this result.
    if (seq !== requestSeq.current) return;
    setData((current) => {
      if (current === null) return fresh;
      const updated = fresh.events.find((event) => event.id === eventId);
      const events =
        updated === undefined
          ? current.events.filter((event) => event.id !== eventId)
          : current.events.map((event) => (event.id === eventId ? updated : event));
      return { count: fresh.count, offen: fresh.offen, events };
    });
    setLoadedAt(new Date());
  }, []);

  // Memoised so the derived lists below only recompute when the payload changes.
  const events = useMemo<readonly VulnerabilityEventRow[]>(() => data?.events ?? [], [data]);

  const bewertungEvent = useMemo(
    () => events.find((event) => event.id === bewertungEventId) ?? null,
    [events, bewertungEventId],
  );
  const handleChanged = useCallback((): void => {
    if (bewertungEventId === null) return;
    void refreshEvent(bewertungEventId).catch((err: unknown) => {
      toast.error(`Zeile konnte nicht neu geladen werden: ${errorMessage(err)}`);
    });
  }, [bewertungEventId, refreshEvent, toast]);

  const counters = useMemo<KpiCounter[]>(() => {
    const betroffen = events.filter((event) => event.bewertung === 'BETROFFEN').length;
    const nichtBetroffen = events.filter((event) => event.bewertung === 'NICHT_BETROFFEN').length;
    const behoben = events.filter((event) => event.bewertung === 'BEHOBEN').length;
    const review = events.filter(needsReview).length;
    const produkteOffen = events.filter(productsOpen).length;
    const offeneMails = sumBy(events, (event) => event.benachrichtigungen_offen);
    const mitOffenerMail = events.filter((event) => event.benachrichtigungen_offen > 0).length;
    const versendeteMails = sumBy(events, (event) => event.benachrichtigungen_versendet);
    return [
      {
        label: 'Events',
        value: data?.count ?? events.length,
        tone: 'neutral',
        title: 'KEV-Treffer in Komponentenversionen insgesamt',
      },
      {
        label: 'Offene Bewertungen',
        value: data?.offen ?? 0,
        tone: (data?.offen ?? 0) > 0 ? 'warn' : 'good',
        title: 'Events, deren Bewertung noch offen ist',
      },
      {
        label: 'Produktbewertung offen',
        value: produkteOffen,
        tone: produkteOffen > 0 ? 'warn' : 'good',
        title: 'Treffer mit mindestens einer Produktversion ohne bestätigte Bewertung',
      },
      {
        label: 'Erneut prüfen',
        value: review,
        tone: review > 0 ? 'warn' : 'good',
        title: 'Eine Produkt- oder Instanzbewertung ist zur erneuten Prüfung markiert oder widerspricht versendeten Benachrichtigungen',
      },
      {
        label: 'Betroffen',
        value: betroffen,
        tone: betroffen > 0 ? 'alert' : 'good',
        title: 'Komponentenbewertung „Betroffen“ — Vorschlag für die Produktversionen',
      },
      {
        label: 'Nicht betroffen',
        value: nichtBetroffen,
        tone: 'neutral',
        title: 'CVE als nicht anwendbar bewertet (mit Begründung im Audit-Trail)',
      },
      {
        label: 'Vom Scan geschlossen',
        value: behoben,
        tone: 'neutral',
        title: 'Vom KEV-Scan automatisch geschlossen (Version nicht mehr im Feld oder CVE entfallen)',
      },
      {
        // Counted per event, like the other tiles, so its filter shows exactly
        // this many rows; the notification totals go in the tooltip.
        label: 'Benachrichtigung offen',
        value: mitOffenerMail,
        tone: mitOffenerMail > 0 ? 'warn' : 'good',
        title: `${offeneMails} Benachrichtigung(en) offen, ${versendeteMails} bereits als versendet gestempelt`,
      },
    ];
  }, [data, events]);

  // Every tile maps onto the assessment filter: each opens its whole category
  // (other filters cleared) and stays marked while exactly it is active. A
  // second click goes back to the default (open ones); on „Offene“, which is
  // the default, it shows all.
  const kpiTiles = useMemo((): KpiItem[] => {
    const only = (bewertung: BewertungFilter): Filters => ({ ...INITIAL_FILTERS, bewertung });
    const targets: Readonly<Record<string, Filters>> = {
      Events: only('ALLE'),
      'Offene Bewertungen': INITIAL_FILTERS,
      'Produktbewertung offen': only('PRODUCTS'),
      'Erneut prüfen': only('REVIEW'),
      Betroffen: only('BETROFFEN'),
      'Nicht betroffen': only('NICHT_BETROFFEN'),
      'Vom Scan geschlossen': only('BEHOBEN'),
      'Benachrichtigung offen': only('MAIL'),
    };
    return counters.map((counter) => {
      const target = targets[counter.label];
      if (!target) return counter;
      const active = sameFilters(filters, target);
      const cleared = target === INITIAL_FILTERS ? only('ALLE') : INITIAL_FILTERS;
      return {
        ...counter,
        ariaLabel: `${counter.label}: ${counter.value} anzeigen`,
        selected: active,
        controls: 'triage-results',
        onClick: () => setFilters(active ? cleared : target),
      };
    });
  }, [counters, filters]);

  const visibleRows = useMemo<VulnerabilityEventRow[]>(() => {
    const scoreFloor = numericFloor(filters.minScore);
    const kundenFloor = numericFloor(filters.minKunden);
    return events.filter((event) => {
      if (!matchesBewertung(event, filters.bewertung)) return false;
      if (!matchesSearch(event, filters.search)) return false;
      if (scoreFloor !== null) {
        const score = parseScore(event.cve.base_score);
        // Events without a score are hidden as soon as a floor is demanded —
        // they cannot be proven to satisfy it.
        if (score === null || score < scoreFloor) return false;
      }
      if (filters.onlyKevListed && !hasNativeKevAssertion(event)) return false;
      if (filters.kevFrom !== '') {
        const added = event.kev.aufgenommen_am;
        // Both sides are 'YYYY-MM-DD', so a string compare is a date compare.
        if (added === null || added < filters.kevFrom) return false;
      }
      if (kundenFloor !== null && event.betroffene_kunden < kundenFloor) return false;
      return true;
    });
  }, [events, filters]);

  const columns = useMemo<DataTableColumn<VulnerabilityEventRow>[]>(() => {
    /** Everyone with queue access may open the levels; saving needs triage.write. */
    function renderActions(event: VulnerabilityEventRow): ReactNode {
      const open = event.bewertung === 'OFFEN' || needsReview(event) || productsOpen(event);
      const label = !canTriage ? 'Ansehen' : open ? 'Bewerten' : 'Öffnen';
      return (
        <div className="triage-actions">
          <button
            type="button"
            className={canTriage && open ? 'primary small' : 'ghost small'}
            title={canTriage ? undefined : missingPermissionTitle('triage.write')}
            onClick={(clickEvent) => {
              // The row itself toggles the detail area — do not fold it here.
              clickEvent.stopPropagation();
              setAssessmentTab(event.bewertung === 'OFFEN' ? 'component' : 'products');
              setBewertungEventId(event.id);
            }}
          >
            {label}
          </button>
        </div>
      );
    }

    return [
      {
        key: 'cve',
        header: 'CVE',
        width: '16%',
        sortValue: (event) => event.cve.id,
        render: (event) => (
          <div className="triage-cve">
            <ChipList>
              <CveLink cve={event.cve.id} />
              {event.cve.aktiv_ausgenutzt ? (
                <Chip tone="warn" title="Aktiv ausgenutzt">
                  aktiv
                </Chip>
              ) : null}
            </ChipList>
            {event.cve.beschreibung ? (
              <div className="prod" title={event.cve.beschreibung}>
                {truncate(event.cve.beschreibung, 70)}
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'score',
        header: 'CVSS',
        align: 'right',
        width: '7%',
        headerTitle: 'CVSS Base Score',
        sortValue: (event) => parseScore(event.cve.base_score),
        render: (event) => <CvssScore score={parseScore(event.cve.base_score)} />,
      },
      {
        key: 'kev',
        header: 'KEV seit',
        width: '10%',
        headerTitle: 'Publikationsdatum der bevorzugten nativen KEV-Assertion',
        sortValue: (event) => event.kev.aufgenommen_am,
        render: (event) => {
          if (event.kev.aufgenommen_am === null) {
            return hasNativeKevAssertion(event)
              ? <Chip tone="alert" title="KEV-Assertion ohne Publikationsdatum">gelistet</Chip>
              : <span className="muted" title="Keine KEV-Assertion oder Abruf fehlgeschlagen">{DASH}</span>;
          }
          return (
            <div>
              {fmtDay(event.kev.aufgenommen_am)}
              {event.kev.frist ? <div className="prod">Frist {fmtDay(event.kev.frist)}</div> : null}
              {event.kev.ransomware === 'Known' ? (
                <div className="prod triage-ransom">Ransomware bekannt</div>
              ) : null}
            </div>
          );
        },
      },
      {
        key: 'komponente',
        header: 'Komponente / Produkte',
        sortValue: (event) => event.komponente.name,
        render: (event) => (
          <div>
            <div className="kunde-name">
              {fmtValue(event.komponente.name)}{' '}
              <span className="muted">{event.komponente.version ?? ''}</span>
            </div>
            <div className="prod">
              {event.komponente.hersteller ? `${event.komponente.hersteller} · ` : ''}
              {event.produktversionen.length === 0
                ? 'in keiner Produktversion'
                : `in ${event.produktversionen.map(produktVersionLabel).join(', ')}`}
            </div>
          </div>
        ),
      },
      {
        key: 'kunden',
        header: 'Betroffene Kunden',
        width: '14%',
        headerTitle: 'Anzahl unterschiedlicher Kunden mit betroffenen Produktinstanzen',
        sortValue: (event) => event.betroffene_kunden,
        render: (event) => {
          const kunden = kundenOf(event);
          if (kunden.length === 0) {
            return <span className="muted">keine</span>;
          }
          const shown = kunden.slice(0, KUNDEN_VISIBLE);
          const hidden = kunden.slice(KUNDEN_VISIBLE);
          const hiddenNames = hidden.map((kunde) => kunde.name ?? '?').join(', ');
          return (
            <div>
              <span className="kunde-name">
                {shown.map((kunde) => kunde.name ?? '?').join(', ')}
              </span>
              {hidden.length > 0 ? (
                <>
                  {' '}
                  <Chip title={`Weitere Kunden: ${hiddenNames}`}>+{hidden.length}</Chip>
                </>
              ) : null}
              <div className="prod">
                {event.betroffene.length}{' '}
                {event.betroffene.length === 1 ? 'Produktinstanz' : 'Produktinstanzen'}
              </div>
            </div>
          );
        },
      },
      {
        key: 'bewertung',
        header: 'Bewertung',
        width: '13%',
        sortValue: (event) => event.bewertung,
        render: (event) => (
          <div>
            <ChipList>
              <Chip tone={BEWERTUNG_TONE[event.bewertung]} title="Komponentenbewertung">{BEWERTUNG_LABELS[event.bewertung]}</Chip>
              {needsReview(event) ? <Chip tone="warn">Erneut prüfen</Chip> : null}
              {event.assessment?.products.contradictions ? <Chip tone="alert" title="Kunden wurden als betroffen benachrichtigt, die Produktbewertung lautet anders">Widerspruch</Chip> : null}
            </ChipList>
            {event.assessment ? (
              <div className="prod">
                Produkte {event.assessment.products.confirmed}/{event.assessment.products.total} bestätigt
                {event.assessment.instances.total
                  ? ` · Instanzen ${event.assessment.instances.confirmed}/${event.assessment.instances.total}`
                  : ''}
              </div>
            ) : null}
            {event.bewertet_von || event.bewertet_am ? (
              <div className="prod">
                {fmtValue(event.bewertet_von)} · {fmtDate(event.bewertet_am)}
              </div>
            ) : null}
            {event.begruendung ? (
              <div className="prod triage-begruendung" title={event.begruendung}>
                „{truncate(event.begruendung, 60)}“
              </div>
            ) : null}
          </div>
        ),
      },
      {
        key: 'benachrichtigung',
        header: 'Benachrichtigung',
        width: '11%',
        sortValue: (event) => event.benachrichtigungen_offen,
        render: (event) => {
          const versendet = event.benachrichtigungen_versendet;
          const offen = event.benachrichtigungen_offen;
          if (versendet === 0 && offen === 0) {
            return event.assessment?.products.affected ? (
              <span className="muted" title="Erst die bestätigte Produktbewertung „Betroffen“ legt Benachrichtigungen an">ausstehend</span>
            ) : (
              <span className="muted">{DASH}</span>
            );
          }
          return (
            <div>
              <Chip tone="good">{versendet} versendet</Chip>
              {offen > 0 ? <div className="prod">{offen} ausstehend</div> : null}
            </div>
          );
        },
      },
      {
        key: 'aktion',
        header: 'Aktion',
        width: '15%',
        render: renderActions,
      },
    ];
  }, [canTriage]);

  if (loading && data === null) {
    return <PageSpinner text="Bewertungs-Warteschlange wird geladen…" />;
  }

  return (
    <>
      <PageHeader
        title="KEV-Bewertungen"
        sub={
          loadedAt === null
            ? undefined
            : `Stand ${loadedAt.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' })}`
        }
      />

      {loadError === null ? null : (
        <Banner kind="error" sub="Prüfen Sie die Verbindung zu CMDBuild.">
          {loadError}
        </Banner>
      )}

      {readOnly ? (
        <Banner kind="info">{readOnlyNotice('triage.write', 'notification.send')}</Banner>
      ) : null}

      <KpiRow counters={kpiTiles} />

      <section id="triage-results" className="card">
        <div className="card-head table-head">
          <h2 title="Standardsortierung: offene Bewertungen zuerst, danach nach Event-ID absteigend.">
            Warteschlange
          </h2>
        </div>
        <div className="table-toolbar">
          <input
            type="search"
            className="triage-search"
            value={filters.search}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, search: changeEvent.target.value }))
            }
            placeholder="CVE, Komponente, Kunde suchen…"
            aria-label="Warteschlange filtern"
          />
          <select
            value={statusFilterValue(filters)}
            onChange={(changeEvent) =>
              setFilters((current) => ({
                ...current,
                ...STATUS_FILTERS[changeEvent.target.value as StatusFilter].flags,
              }))
            }
            aria-label="Nach Status filtern"
          >
            {(Object.keys(STATUS_FILTERS) as StatusFilter[]).map((key) => (
              <option key={key} value={key}>
                {STATUS_FILTERS[key].label}
              </option>
            ))}
          </select>
          <select
            value={filters.minScore}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, minScore: changeEvent.target.value }))
            }
            aria-label="Min. CVSS"
          >
            {SCORE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          <label className="triage-inline-field">
            <span>KEV ab</span>
            <input
              type="date"
              value={filters.kevFrom}
              onChange={(changeEvent) =>
                setFilters((current) => ({ ...current, kevFrom: changeEvent.target.value }))
              }
              aria-label="KEV-Aufnahme ab"
            />
          </label>
          <input
            type="number"
            className="triage-min-kunden"
            min={0}
            step={1}
            value={filters.minKunden}
            onChange={(changeEvent) =>
              setFilters((current) => ({ ...current, minKunden: changeEvent.target.value }))
            }
            placeholder="Min. Kunden"
            aria-label="Min. Kunden"
            title="Mindestanzahl betroffener Kunden"
          />
          <div className="table-toolbar-options">
            {filters.search !== INITIAL_FILTERS.search ||
            filters.bewertung !== INITIAL_FILTERS.bewertung ||
            filters.onlyKevListed !== INITIAL_FILTERS.onlyKevListed ||
            filters.minScore !== INITIAL_FILTERS.minScore ||
            filters.kevFrom !== INITIAL_FILTERS.kevFrom ||
            filters.minKunden !== INITIAL_FILTERS.minKunden ? (
              <button type="button" className="link" onClick={() => setFilters(INITIAL_FILTERS)}>
                Filter zurücksetzen
              </button>
            ) : null}
            <span className="table-count" aria-live="polite">
              {visibleRows.length === events.length
                ? countLabel(events.length, 'Bewertung', 'Bewertungen')
                : `${visibleRows.length} von ${countLabel(events.length, 'Bewertung', 'Bewertungen')}`}
            </span>
          </div>
        </div>

        {events.length === 0 && loadError === null ? (
          <EmptyState title="Noch keine KEV-Bewertungen vorhanden">
            Bewertungen entstehen nach dem KEV-Abgleich für betroffene ausgelieferte Komponenten.
          </EmptyState>
        ) : (
          <DataTable
            rows={visibleRows}
            columns={columns}
            rowKey={(event) => event.id}
            mobileBreakpoint={960}
            pageSize={10}
            paginationResetKey={JSON.stringify(filters)}
            resetPageOnRowsChange={false}
            revealRowKey={bewertungEventId}
            caption="Bewertungs-Warteschlange der KEV-Treffer"
            emptyText="Keine Bewertungen für die aktuelle Auswahl. Filter anpassen oder im Statusfilter „Alle Bewertungen“ wählen."
            rowClassName={(event) => (event.bewertung === 'OFFEN' ? 'triage-row-open' : undefined)}
            renderDetail={(event) => (
              <div className="triage-detail">
                <EventCveFacts event={event} />
                <EventScopeFacts event={event} />
                <EventAuditTrail event={event} />
                <EventInstanzen event={event} />
              </div>
            )}
          />
        )}
      </section>

      {bewertungEvent === null ? null : (
        <AssessmentDialog
          key={bewertungEvent.id}
          event={bewertungEvent}
          bewerter={bewerter}
          canWrite={canTriage}
          initialTab={assessmentTab}
          onClose={() => setBewertungEventId(null)}
          onChanged={handleChanged}
        />
      )}
    </>
  );
}

export default TriagePage;
